import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Camera, FilmSlate, PenNib, PlayCircle } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const TYPE_META = {
    photo: { label: "Photo", Icon: Camera, accent: "yellow" },
    video: { label: "Vidéo", Icon: FilmSlate, accent: "yellow" },
    logo: { label: "Branding", Icon: PenNib, accent: "green" },
};

export function resolveSrc(src) {
    if (!src) return "";
    if (/^https?:\/\//i.test(src)) return src;
    let path = src.startsWith("/") ? src : `/${src}`;
    // Backward-compat with items uploaded to the old ephemeral disk mount
    // (files no longer exist — those items are cleaned via the purge_legacy admin endpoint)
    if (path.startsWith("/uploads/")) {
        path = `/api${path}`;
    }
    return `${BACKEND_URL}${path}`;
}

export default function Showreel() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await axios.get(`${BACKEND_URL}/api/showreel`);
                if (!cancelled) setItems(data);
            } catch (err) {
                console.error("Showreel load failed:", err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <section
            id="showreel"
            data-testid="showreel-section"
            className="relative py-24 md:py-32 px-6 md:px-12 lg:px-16 border-t border-scd"
        >
            <div className="grid md:grid-cols-12 gap-8 mb-14 md:mb-20">
                <div className="md:col-span-4">
                    <span className="label-eyebrow text-yellow">— Nos réalisations</span>
                </div>
                <div className="md:col-span-8">
                    <h2 className="section-heading text-cream" data-testid="showreel-heading">
                        Photos, vidéos
                        <br />
                        & <span className="text-yellow italic">identités visuelles.</span>
                    </h2>
                    <p className="mt-6 max-w-2xl text-cream/75 text-lg">
                        Un aperçu de ce que nous produisons quand on ne construit
                        pas de sites web.
                    </p>
                </div>
            </div>

            {loading ? (
                <p
                    data-testid="showreel-loading"
                    className="label-eyebrow text-cream/50 text-center py-20"
                >
                    Chargement des réalisations…
                </p>
            ) : items.length === 0 ? (
                <div
                    data-testid="showreel-empty"
                    className="border border-dashed border-scd rounded-sm py-16 md:py-24 text-center"
                >
                    <p className="label-eyebrow text-yellow">— Bientôt en ligne</p>
                    <p className="mt-4 text-cream/70 max-w-md mx-auto">
                        De nouvelles réalisations sont en cours de préparation.
                        Reviens bientôt ou{" "}
                        <a
                            href="#contact"
                            className="underline text-cream hover:text-yellow"
                        >
                            parlons-en directement
                        </a>
                        .
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[180px] md:auto-rows-[220px] gap-3 md:gap-4">
                    {items.map((it, i) => (
                        <ShowreelTile key={it.id} item={it} index={i} />
                    ))}
                </div>
            )}
        </section>
    );
}

function ShowreelTile({ item, index }) {
    const meta = TYPE_META[item.type] || TYPE_META.photo;
    const span = item.span || "lg:col-span-4";
    const src = resolveSrc(item.src);
    const poster = resolveSrc(item.poster);

    return (
        <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.6, delay: index * 0.05 }}
            data-testid={`showreel-item-${item.id}`}
            className={`group relative col-span-2 md:col-span-6 ${span} overflow-hidden rounded-sm border border-scd bg-elev`}
        >
            {item.type === "video" ? (
                <VideoTile src={src} poster={poster} />
            ) : (
                <img
                    src={src}
                    alt={item.title}
                    loading="lazy"
                    onError={(e) => {
                        // Graceful fallback for broken/missing images (e.g. legacy uploads)
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent && !parent.querySelector(".fallback-placeholder")) {
                            const ph = document.createElement("div");
                            ph.className =
                                "fallback-placeholder w-full h-full flex items-center justify-center bg-gradient-to-br from-[#3b5838]/25 to-[#ffd140]/25 text-cream/50 font-display text-3xl";
                            ph.textContent = "112";
                            parent.appendChild(ph);
                        }
                    }}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
            )}

            {/* Dark gradient + label overlay (kept dark for image contrast) */}
            <div
                aria-hidden
                className="absolute inset-0 bg-gradient-to-t from-[#272727]/85 via-[#272727]/15 to-transparent pointer-events-none"
            />
            <div className="absolute top-4 left-4 flex items-center gap-2">
                <span
                    className={`inline-flex items-center justify-center w-7 h-7 rounded-full ${
                        meta.accent === "yellow" ? "bg-yellow text-[#272727]" : "bg-green text-[#faf7f1]"
                    }`}
                >
                    <meta.Icon size={13} weight="bold" />
                </span>
                <span className="label-eyebrow text-[#faf7f1]/95">{meta.label}</span>
            </div>
            {item.title && (
                <div className="absolute bottom-4 left-4 right-4">
                    <span className="font-display text-base md:text-lg text-[#faf7f1] leading-tight">
                        {item.title}
                    </span>
                </div>
            )}
        </motion.div>
    );
}

function VideoTile({ src, poster }) {
    const ref = useRef(null);
    const [hover, setHover] = useState(false);

    useEffect(() => {
        const v = ref.current;
        if (!v) return;
        if (hover) {
            v.play().catch(() => {});
        } else {
            v.pause();
            v.currentTime = 0;
        }
    }, [hover]);

    return (
        <div
            className="relative w-full h-full cursor-pointer"
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onTouchStart={() => setHover((h) => !h)}
        >
            <video
                ref={ref}
                src={src}
                poster={poster}
                muted
                loop
                playsInline
                preload="metadata"
                className="w-full h-full object-cover"
            />
            {!hover && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                    <PlayCircle
                        size={56}
                        weight="duotone"
                        className="text-[#faf7f1] drop-shadow-lg"
                    />
                </div>
            )}
        </div>
    );
}
